import type { EnvKind } from '@/api/catalogApi'

/** The slice of a client row this module reads. */
export interface ClientWithEnvironments {
  id: string
  name: string
  environments: { name: string; kind: EnvKind; status: string; product: { key: string; name: string } }[]
}

export interface ClientProduct {
  key: string
  name: string
  /** The client's live environments of this product ("Prod", "Validation"). */
  environments: { name: string; kind: EnvKind }[]
}

/** The filter value for clients that run no product yet. */
export const NO_PRODUCT = 'none'

/** The products a client runs (retired environments left out), by product name. */
export function clientProducts(client: ClientWithEnvironments): ClientProduct[] {
  const byKey = new Map<string, ClientProduct>()
  for (const env of client.environments) {
    if (env.status === 'retired') continue
    const p = byKey.get(env.product.key) ?? { key: env.product.key, name: env.product.name, environments: [] }
    p.environments.push({ name: env.name, kind: env.kind })
    byKey.set(p.key, p)
  }
  return [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
}

/** Clients running `product` (or none, for NO_PRODUCT); everything for null. */
export function filterByProduct<T extends ClientWithEnvironments>(clients: T[], product: string | null): T[] {
  if (!product) return clients
  return clients.filter((c) => {
    const keys = clientProducts(c).map((p) => p.key)
    return product === NO_PRODUCT ? keys.length === 0 : keys.includes(product)
  })
}

export interface ProductOption {
  key: string
  name: string
  count: number
}

/** Filter options: every product the clients run, with how many run it; "no product" last when any. */
export function productOptions(clients: ClientWithEnvironments[]): ProductOption[] {
  const byKey = new Map<string, ProductOption>()
  let none = 0
  for (const c of clients) {
    const products = clientProducts(c)
    if (products.length === 0) none++
    for (const p of products) {
      const o = byKey.get(p.key) ?? { key: p.key, name: p.name, count: 0 }
      o.count++
      byKey.set(p.key, o)
    }
  }
  const options = [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name))
  return none ? [...options, { key: NO_PRODUCT, name: '', count: none }] : options
}

export interface ProductGroup<T> {
  /** null = clients that run no product yet. */
  product: { key: string; name: string } | null
  clients: T[]
}

/**
 * Clients grouped under each product they run, by product name, with the
 * clients that run none last. A client on two products sits in both groups.
 */
export function groupByProduct<T extends ClientWithEnvironments>(clients: T[]): ProductGroup<T>[] {
  const groups = new Map<string, ProductGroup<T>>()
  const none: T[] = []
  for (const c of clients) {
    const products = clientProducts(c)
    if (products.length === 0) none.push(c)
    for (const p of products) {
      const g = groups.get(p.key) ?? { product: { key: p.key, name: p.name }, clients: [] }
      g.clients.push(c)
      groups.set(p.key, g)
    }
  }
  const sorted = [...groups.values()].sort((a, b) => a.product!.name.localeCompare(b.product!.name))
  return none.length ? [...sorted, { product: null, clients: none }] : sorted
}
